/**
 * ReviewCard — Premium review display component
 */
import React, { useState } from 'react';
import { ThumbsUp, Flag, MoreHorizontal, Check, ChevronDown } from 'lucide-react';
import { Rating } from '../../shared';
import { REVIEW_BADGES, USER_LEVELS, formatRelativeTime } from '../../../data/reviews';

export function ReviewCard({
  review,
  onHelpful,
  onReport,
  language = 'th',
  className = ''
}) {
  const [helpfulVoted, setHelpfulVoted] = useState(false);
  const [helpfulCount, setHelpfulCount] = useState(review.helpful || 0);
  const [expanded, setExpanded] = useState(false);

  const userLevel = USER_LEVELS[review.userLevel] || USER_LEVELS['Newcomer'];
  const isLongContent = review.content.length > 300;
  const displayContent = expanded || !isLongContent
    ? review.content
    : review.content.slice(0, 300) + '...';

  const handleHelpful = () => {
    if (helpfulVoted) return;
    setHelpfulVoted(true);
    setHelpfulCount(prev => prev + 1);
    onHelpful?.(review.id);
  };

  return (
    <div className={`bg-white border border-neutral-100 rounded-2xl p-5 transition-all duration-300 hover:shadow-lg ${className}`}>
      {/* Header */}
      <div className="flex items-start gap-4">
        {/* Avatar */}
        <div
          className="w-11 h-11 rounded-full flex items-center justify-center text-white font-semibold text-[14px] flex-shrink-0"
          style={{ backgroundColor: userLevel.color }}
        >
          {review.userName.charAt(0).toUpperCase()}
        </div>

        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-semibold text-neutral-900">{review.userName}</span>
            <span
              className="text-[11px] px-2 py-0.5 rounded-full font-medium"
              style={{
                backgroundColor: `${userLevel.color}20`,
                color: userLevel.color
              }}
            >
              {review.userLevel}
            </span>
            {review.badges?.includes('verified') && (
              <span className="verified-badge">
                <Check size={12} />
                {language === 'th' ? 'ยืนยันแล้ว' : 'Verified'}
              </span>
            )}
          </div>
          <div className="flex items-center gap-3 mt-1">
            <Rating value={review.rating} size="sm" />
            <span className="text-[12px] text-neutral-400">
              {formatRelativeTime(review.createdAt)}
            </span>
          </div>
        </div>

        <button className="p-2 text-neutral-400 hover:text-neutral-600 hover:bg-neutral-100 rounded-lg transition-colors">
          <MoreHorizontal size={18} />
        </button>
      </div>

      {/* Content */}
      <div className="mt-4">
        <h4 className="font-semibold text-neutral-900 mb-2">{review.title}</h4>
        <p className="text-[14px] text-neutral-700 leading-relaxed whitespace-pre-wrap">
          {displayContent}
        </p>
        {isLongContent && (
          <button
            onClick={() => setExpanded(!expanded)}
            className="inline-flex items-center gap-1 text-[13px] text-brand-500 font-medium mt-2 hover:text-brand-600"
          >
            {expanded
              ? (language === 'th' ? 'แสดงน้อยลง' : 'Show less')
              : (language === 'th' ? 'อ่านเพิ่มเติม' : 'Read more')
            }
            <ChevronDown size={14} className={`transition-transform ${expanded ? 'rotate-180' : ''}`} />
          </button>
        )}
      </div>

      {/* Category Ratings */}
      {review.categoryRatings && (
        <div className="mt-4 flex flex-wrap gap-4">
          {Object.entries(review.categoryRatings).map(([key, value]) => {
            if (!value) return null;
            const labels = {
              food: language === 'th' ? 'อาหาร' : 'Food',
              service: language === 'th' ? 'บริการ' : 'Service',
              atmosphere: language === 'th' ? 'บรรยากาศ' : 'Atmosphere',
              value: language === 'th' ? 'ความคุ้มค่า' : 'Value'
            };
            return (
              <div key={key} className="flex items-center gap-2">
                <span className="text-[12px] text-neutral-500">{labels[key]}</span>
                <Rating value={value} size="sm" />
              </div>
            );
          })}
        </div>
      )}

      {/* Dishes */}
      {review.dishes?.length > 0 && (
        <div className="mt-4 flex flex-wrap gap-2">
          {review.dishes.map(dish => (
            <span
              key={dish}
              className="px-3 py-1 bg-neutral-50 border border-neutral-100 rounded-full text-[12px] text-neutral-600"
            >
              {dish}
            </span>
          ))}
        </div>
      )}

      {/* Photos */}
      {review.images?.length > 0 && (
        <div className="mt-4 flex gap-2 overflow-x-auto pb-2">
          {review.images.slice(0, 4).map((image, index) => (
            <div
              key={index}
              className="flex-shrink-0 w-24 h-24 rounded-xl overflow-hidden bg-neutral-100"
            >
              <img
                src={typeof image === 'string' ? image : image.preview}
                alt=""
                className="w-full h-full object-cover hover:scale-105 transition-transform duration-300"
              />
            </div>
          ))}
          {review.images.length > 4 && (
            <div className="flex-shrink-0 w-24 h-24 rounded-xl bg-neutral-100 flex items-center justify-center">
              <span className="text-[13px] font-medium text-neutral-500">
                +{review.images.length - 4}
              </span>
            </div>
          )}
        </div>
      )}

      {/* Footer */}
      <div className="mt-4 pt-4 border-t border-neutral-100 flex items-center justify-between">
        <button
          onClick={handleHelpful}
          className={`helpful-btn ${helpfulVoted ? 'voted' : ''}`}
        >
          <ThumbsUp size={14} className={helpfulVoted ? 'fill-current' : ''} />
          <span>{language === 'th' ? 'เป็นประโยชน์' : 'Helpful'}</span>
          <span className="font-medium">{helpfulCount}</span>
        </button>

        <button
          onClick={() => onReport?.(review.id)}
          className="text-[12px] text-neutral-400 hover:text-neutral-600 flex items-center gap-1"
        >
          <Flag size={12} />
          {language === 'th' ? 'รายงาน' : 'Report'}
        </button>
      </div>

      {/* Reply */}
      {review.reply && (
        <div className="mt-4 p-4 bg-neutral-50 rounded-xl">
          <div className="flex items-center gap-2 mb-2">
            <span className="font-semibold text-[13px] text-neutral-800">
              {language === 'th' ? 'การตอบกลับจากร้าน' : 'Response from restaurant'}
            </span>
          </div>
          <p className="text-[13px] text-neutral-600">{review.reply.content}</p>
          <span className="text-[11px] text-neutral-400 mt-2 block">
            {formatRelativeTime(review.reply.createdAt)}
          </span>
        </div>
      )}
    </div>
  );
}

export default ReviewCard;
